/**
 * Orders the land points: weight of the detector in the method (0–3) × strength ×
 * freshness. A point that hits a deal-breaker the method ticked takes weight 3 and comes
 * first, whatever its score. Ties: attention, positive, to know. Weight 0 drops a point
 * that hits no deal-breaker. The model never changes this order.
 */
import type {
  LandTemplateId,
  LandTriageCandidate,
  LandTriageDetectorId,
  LandTriageMethodView,
  LandTriageScoredCandidate,
} from '@popety-io/types'

type Weight = 0 | 1 | 2 | 3
type Weights = Record<LandTriageDetectorId, Weight>

const ORDER: LandTriageDetectorId[] = [
  'zone',
  'rights',
  'engaged',
  'pending',
  'size',
  'setback',
  'shape',
  'slope',
  'built',
  'heritage',
  'owners',
  'restr',
  'hazard',
  'noise',
  'transport',
  'mut',
  'listing',
  'study',
  'works',
  'height',
]
const w = (...ws: Weight[]): Weights =>
  Object.fromEntries(ORDER.map((d, i) => [d, ws[i]])) as Weights

/** Template weights, in ORDER (23.10.2026, owner: "go with your recommendations"). */
export const LAND_TEMPLATE_WEIGHTS: Record<LandTemplateId, Weights> = {
  promoteur: w(3, 3, 3, 3, 2, 3, 2, 2, 2, 3, 2, 3, 2, 1, 1, 1, 2, 2, 1, 1),
  agence: w(3, 2, 2, 3, 2, 1, 1, 1, 2, 2, 3, 2, 2, 1, 2, 3, 3, 1, 2, 1),
  banque: w(3, 1, 3, 2, 1, 2, 1, 2, 2, 2, 3, 3, 3, 2, 2, 2, 1, 2, 1, 1),
  regie: w(3, 1, 2, 2, 1, 1, 0, 1, 3, 2, 3, 2, 2, 2, 2, 2, 2, 0, 2, 1),
}

export const LAND_TRIAGE_DETECTORS = ORDER

const KIND_ORDER = { att: 0, pos: 1, info: 2 } as const

export const scoreLandCandidates = (
  cands: LandTriageCandidate[],
  method: LandTriageMethodView
): LandTriageScoredCandidate[] =>
  cands
    .map((c) => {
      const weight = c.killer ? 3 : method.weights[c.detector]
      return {
        ...c,
        weight,
        score: Math.round(weight * c.strength * c.freshness * 100) / 100,
      }
    })
    .filter((c) => c.weight > 0)
    .sort(
      (a, b) =>
        Number(!!b.killer) - Number(!!a.killer) ||
        b.score - a.score ||
        KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
        a.id.localeCompare(b.id)
    )
    .slice(0, 7)
